<template>
  <div id="operations">
    <OperationsBreadcrumb :links="links" />
    <div v-if="$i18n.locale === 'pe'">
      <Message message="" icon="coming_soon_cl.svg" :as-card="true" />
    </div>
    <div v-if="isI18n & ($i18n.locale === 'cl' || $i18n.locale === 'br')">
      <div class="card-item mt-6">
        <Spinner v-if="isLoading" />
        <div class="operations-header mb-2">
          <OperationsButtons
            v-if="!isLoading"
            :hide="true"
            is-upload-active="true"
            @refresh="getSummaryOperationsByType()" />
          <b-tag v-if="!isLoading && $i18n.locale === 'br'" icon="circle-check" type="is-light is-warning" class="" rounded>
            {{ verifiedOperations.length }} {{ verifiedOperations.length > 1 ? 'operações' : 'operação' }} verificada{{ verifiedOperations.length > 1 ? 's' : '' }} de {{ summaryOperation.length }}
          </b-tag>
        </div>
        <b-table
          v-if="!isLoading"
          :data="summaryOperation"
          :paginated="isPaginated"
          :per-page="perPage">
          <b-table-column v-slot="props" field="clientName" :label="$t('clientName')" searchable sortable>
            <b-button
              :id="`client-name-${props.row.idNumber}`"
              :disabled="!checkUserFeature('element.operations.button.view-operation')"
              type="is-primary is-light is-size-6"
              @click="clickedOnOperation(props.row._id, props.row)">
              {{ props.row.clientName }}
            </b-button>
          </b-table-column>
          <b-table-column v-slot="props" field="idNumber" :label="$t('idNumber')" searchable sortable>
            {{ props.row.idNumber }}
          </b-table-column>
          <b-table-column v-slot="props" field="companyOperationId" :label="$t('companyOperationId')" searchable sortable>
            {{ props.row.companyOperationId }}
          </b-table-column>
          <b-table-column v-slot="props" field="type" :label="$t('type')" searchable sortable>
            {{ props.row.type }}
          </b-table-column>
          <b-table-column v-slot="props" field="approvedAmount" :label="$t('approvedAmount')" searchable sortable>
            {{ props.row.approvedAmount }}
          </b-table-column>
          <b-table-column v-slot="props" field="investor" :label="$t('investor')" searchable sortable>
            {{ props.row.investor }}
          </b-table-column>
          <b-table-column v-slot="props" field="termAsMonths" :label="$t('termAsMonths')" sortable>
            <b-progress
              :value="props.row.paymentsMade === undefined ? 0 : props.row.paymentsMade"
              type="is-primary"
              size="is-medium"
              :max="props.row.termAsMonths"
              show-value>
              <span class="payments-progress">{{ props.row.paymentsMade === undefined ? '0' : props.row.paymentsMade }} / {{ props.row.termAsMonths }}</span>
            </b-progress>
          </b-table-column>
          <b-table-column v-slot="props" field="isActive" :label="$t('isActive')" sortable>
            <div>
              <b-field class="is-flex is-align-items-center">
                <b-switch
                  :disabled="!checkUserFeature('element.operations.switch.deactivate-operation')"
                  :value="props.row.isActive"
                  size="is-small"
                  @input="clickedOnDeactivateOperation(props.row._id)" />
                <i v-if="props.row.isVerified" class="has-text-primary fa-regular fa-circle-check" />
              </b-field>
            </div>
          </b-table-column>
        </b-table>
      </div>
      <OperationDeactivateConfirmModal
        :operation="selectedOperation"
        :state-user="stateUser"
        :is-component-modal-active="isComponentModalActive"
        @process="clickedOnProcessDeactivateOperation"
        @cancelProcess="cancelProcess" />
      <OperationDeactivateResultModal
        :operation="selectedOperation"
        :result="deactivateResult"
        :is-component-modal-active="isComponentResultModalActive"
        @cancelProcess="cancelProcess" />
    </div>
  </div>
</template>

<script>
import Spinner from '@/ui/components/Spinner.vue';
import Message from '@/ui/components/Message.vue';
import { getOperation, updateOperationState, getSummaryOperations } from '@/application/services/api-clients/operations';
import { getClient } from '@/application/services/api-clients/clients';
import { getUserState } from '@/application/services/api-clients/users';
import OperationsButtons from '@/ui/components/internal/operations/OperationsButtons.vue';
import OperationDeactivateConfirmModal from '@/ui/components/internal/operations/OperationDeactivateConfirmModal.vue';
import OperationDeactivateResultModal from '@/ui/components/internal/operations/OperationDeactivateResultModal.vue';
import OperationsBreadcrumb from '@/ui/views/internal/operations/OperationsBreadcrumb.vue';
import userFeatures from '@/ui/mixins/user-features';
import { formatNumberByCountry } from '@/infrastructure/format';

export default {
  name: 'Operations',
  components: {
    Spinner,
    Message,
    OperationsButtons,
    OperationDeactivateConfirmModal,
    OperationDeactivateResultModal,
    OperationsBreadcrumb
  },
  data() {
    return {
      isLoading: false,
      isComponentModalActive: false,
      isComponentResultModalActive: false,
      selectedOperation: {},
      deactivateResult: {},
      stateUser: null,
      typeFilter: '',
      isPaginated: true,
      perPage: 10,
      summaryOperation: [],
      verifiedOperations: [],
      types: [
        'MORTGAGE',
        'HOME_EQUITY',
        'PARKING',
        'COMPLEMENTARY',
        'SME'
      ],
      links: []
    };
  },
  computed: {
    isI18n() { return ['br', 'cl', 'pe'].includes(this.$i18n.locale); }
  },
  async mounted() {
    await this.getSummaryOperationsByType();
    this.verifiedOperations = this.summaryOperation?.filter(object => object.isVerified);
  },
  methods: {
    async getSummaryOperationsByType() {
      this.isLoading = true;
      const result = await getSummaryOperations(this.types);
      this.summaryOperation = result.map(operation => ({
        _id: operation._id,
        type: operation.type,
        subType: operation.subType,
        clientName: operation.clientName,
        idNumber: operation.idNumber,
        companyOperationId: operation.companyOperationId,
        approvedAmount: formatNumberByCountry({ number: operation.approvedAmount,
          countryIn: this.$i18n.locale,
          usePrefix: true,
          fractionDigits: 0,
          currencyType: this.setCurrancyType(this.$i18n.locale, operation.type) }),
        paymentsMade: operation.paymentsMade,
        termAsMonths: operation.termAsMonths,
        investor: operation.investorShortName,
        isActive: operation.isActive,
        isVerified: operation.isVerified
      }));
      this.isLoading = false;
    },
    setCurrancyType(country, operationType) {
      const currancyType = country === 'cl' && (operationType === 'MORTGAGE' || operationType === 'PARKING') ? 'primary' : 'secondary';
      return currancyType;
    },
    clickedOnOperation(operationId, summaryOperation) {
      this.$router.push({
        name: 'operation-management',
        params: { operationId, summaryOperation }
      });
    },
    async clickedOnDeactivateOperation(operationId) {
      const operation = await getOperation(operationId);
      const client = await getClient(operation.clientId);
      this.stateUser = await getUserState(client.email);
      this.selectedOperation = { ...operation, client: { ...client } };
      this.showConfirmModal();
    },
    showConfirmModal() {
      this.isComponentModalActive = true;
    },
    async cancelProcess() {
      this.isComponentModalActive = false;
      this.isComponentResultModalActive = false;
      await this.getSummaryOperationsByType();
    },
    async clickedOnProcessDeactivateOperation(operation, enableOrDisableUser, enableOrDisableOperation, reason) {
      // Si enableOrDisableOperation es falso, entonces desactiva operación y si userState es verdadero, entonces desactiva usuario
      let userState = '';
      if (enableOrDisableUser) {
        userState = enableOrDisableOperation ? 'enable' : 'disable';
      }
      const result = {
        pendingTransactions: undefined,
        disabledUser: undefined,
        operationUpdated: undefined,
        clientBalance: undefined,
        error: []
      };
      try {
        const operationUpdated = await updateOperationState(operation._id, reason, enableOrDisableOperation, userState, operation.client.email, operation.clientId);
        if (operationUpdated) {
          operationUpdated.forEach(item => {
            if (item.from === 'Transactions' && item.type === 'error') {
              result.pendingTransactions = true;
              result.disabledUser = false;
              result.error.push(item.value);
            }
            if (item.from === 'User' && item.type === 'success') {
              result.disabledUser = true;
            }
            if (item.from === 'User' && item.type === 'error') {
              result.disabledUser = false;
              result.error.push(item.value);
            }
            if (item.from === 'Operations' && item.type === 'success') {
              result.operationUpdated = true;
            }
            if (item.from === 'Operations' && item.type === 'error') {
              result.disabledUser = false;
              result.error.push(item.value);
            }
            if (item.from === 'Clients' && item.type === 'success') {
              result.clientBalance = item.value;
            }
            if (item.from === 'Clients' && item.type === 'error') {
              result.error.push(item.value);
            }
          });
        }
      } catch (error) {
        result.error.push(error);
      }
      this.isComponentModalActive = false;
      this.deactivateResult = result;
      this.isComponentResultModalActive = true;
    },
    checkUserFeature(name) {
      return userFeatures.methods.checkUserFeature(name, this.$store);
    }
  }
};
</script>

<style scoped>
.card-item {
  margin-bottom: 1rem;
}
.operations-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.operations-items {
  max-width: 100%;
}

.payments-progress {
  color: black;
  font-weight: 100;
}
</style>
<style>
@media screen and (min-width: 769px) {
  #operations .table-wrapper{
    overflow: hidden;
  }
}
</style>

<i18n>
{
  "cl": {
    "operationsTitle": "Operaciones",
    "filterText": "Hay ",
    "withOutResult":"ninguna coincide con el filtro",
    "showing": "mostrando",
    "seeDetail": "Ver Detalle",
    "operations": "operaciones",
    "allFilter": "Todas",
    "mortgageFilter": "Hipotecario",
    "homeEquityFilter": "Home equity",
    "complementaryFilter": "Complementario",
    "parkingFilter": "Estacionamiento",
    "clientName": "Nombre",
    "idNumber": "RUT",
    "companyOperationId": "Id",
    "type": "Tipo",
    "approvedAmount": "Monto",
    "investor": "Owner",
    "termAsMonths": "Cuotas",
    "isActive": "Estado"
  },
  "pe": {
    "operationsTitle": "Operaciones",
    "filterText": "Hay ",
    "withOutResult":"ninguna coincide con el filtro",
    "showing": "mostrando",
    "seeDetail": "Ver Detalle",
    "operations": "operaciones",
    "clientName": "Nombre",
    "idNumber": "DNI",
    "companyOperationId": "Id",
    "type": "Tipo",
    "approvedAmount": "Monto",
    "investor": "Owner",
    "termAsMonths": "Cuotas",
    "isActive": "Estado"
  },
  "br": {
    "operationsTitle": "Operações",
    "filterText": "Tem ",
    "withOutResult":"ninguna coincide com o filtro",
    "showing": "mostrando",
    "seeDetail": "Ver Detalle",
    "operations": "operações",
    "clientName": "Nome",
    "idNumber": "CPF",
    "companyOperationId": "Id",
    "type": "Tipo",
    "approvedAmount": "Valor",
    "investor": "Owner",
    "termAsMonths": "Parcelas",
    "isActive": "Estado"
  }
}
</i18n>
